import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Redirects unauthenticated visitors to /auth, preserving the original
 * destination (path + query) as a `redirect` query param so the Auth page
 * can send them back after sign-in.
 *
 * This is the single source of truth for protected-route gating across the
 * monolithic NutriMart shell. Every page that lives under <NutriMart /> is
 * covered by this hook — /myaccount, /profile, /orders, /admin, etc.
 *
 * Visiting "/" without auth produces a bare "/auth" (no noisy ?redirect=%2F).
 */
/**
 * Pages that REQUIRE authentication. Everything else (home/dashboard,
 * product listing, product detail, learn, stores, community browsing…)
 * is accessible to anonymous visitors so they can explore the demo
 * without being forced through sign-up.
 */
const PROTECTED_PAGES = new Set<string>([
  "myaccount", "profile", "orders", "favorites",
  "admin", "vendor", "notifications", "rewards", "referral",
  "tracker", "scanner", "healthGoals", "myPlan", "healthReport",
  "wearable", "aiAgent", "bodyScan",
]);

export function useAuthRedirectToLogin(opts: {
  user: unknown | null;
  loading: boolean;
  page?: string;
}) {
  const { user, loading, page } = opts;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || user) return;
    if (page && !PROTECTED_PAGES.has(page)) return;
    const dest = `${location.pathname}${location.search}`;
    const qs = dest && dest !== "/" ? `?redirect=${encodeURIComponent(dest)}` : "";
    navigate(`/auth${qs}`, { replace: true });
  }, [loading, user, page, navigate, location.pathname, location.search]);
}